// src/utils/geocoding.js
// Helper Nominatim (OpenStreetMap) — recherche villes et adresses internationales

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const USER_AGENT = 'HmongHearts/1.0 (contact@hmonghearts.com)';
const THROTTLE_MS = 1000;

let lastRequestTime = 0;

const throttledFetch = async (url) => {
  const now = Date.now();
  const elapsed = now - lastRequestTime;
  if (elapsed < THROTTLE_MS) {
    await new Promise((resolve) => setTimeout(resolve, THROTTLE_MS - elapsed));
  }
  lastRequestTime = Date.now();
  return fetch(url, {
    headers: { 'User-Agent': USER_AGENT },
  });
};

const extractCity = (address) => {
  return address?.city || address?.town || address?.village || address?.municipality || '';
};

/**
 * Recherche de villes (municipality) — pour StepLocalisation et AnnonceFormModal
 * @param {string} query
 * @returns {Promise<Array<{city: string, postcode: string, lat: number, lng: number, label: string}>>}
 */
export const searchCities = async (query) => {
  try {
    const url = `${NOMINATIM_URL}?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&featuretype=city`;
    const res = await throttledFetch(url);
    const data = await res.json();
    return (data || []).map((item) => {
      const city = extractCity(item.address) || item.display_name.split(',')[0];
      const postcode = item.address?.postcode || '';
      return {
        city,
        postcode,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        label: postcode ? `${city} (${postcode})` : city,
      };
    });
  } catch {
    return [];
  }
};

/**
 * Recherche d'adresses complètes — pour EventFormModal
 * @param {string} query
 * @returns {Promise<Array<{name: string, address: string, city: string, postcode: string, lat: number, lng: number}>>}
 */
export const searchAddresses = async (query) => {
  try {
    const url = `${NOMINATIM_URL}?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5`;
    const res = await throttledFetch(url);
    const data = await res.json();
    return (data || []).map((item) => {
      const city = extractCity(item.address) || '';
      const postcode = item.address?.postcode || '';
      const road = item.address?.road || '';
      const houseNumber = item.address?.house_number || '';
      const name = [houseNumber, road].filter(Boolean).join(' ') || item.display_name.split(',')[0];
      return {
        name,
        address: item.display_name,
        city,
        postcode,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
      };
    });
  } catch {
    return [];
  }
};
